import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
} from "@mui/material";
import {
  CheckCircle,
  Pending,
  ShoppingCart,
  Refresh,
  AccountBalance,
  Cancel,
  Visibility,
  TrendingUp,
  ExpandMore,
  LocalShipping,
  Payment,
  Person,
  ShoppingBag,
  Inventory,
  Info,
  Star,
  Palette,
  Straighten,
  Scale,
  Build,
  Security,
  Schedule,
  LocationOn,
  Phone,
  Email,
  CreditCard,
  Discount,
  Download,
  Receipt,
  PictureAsPdf,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  orderBy,
  where,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { cartOrderQualifiesForInvoice } from "../utils/cartOrderInvoice";
import { confirmCartOrder } from "../services/CartOrderConfirmationService";

function orderNeedsAdminPaymentApproval(order) {
  if (!order || order.orderStatus !== "pending") return false;
  if (order.paymentMethod === "cashfree") return false;
  if (order.paymentDetails?.cashfreeOrderId) return false;
  return true;
}

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    order: null,
  });
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    order: null,
  });
  const [viewDialog, setViewDialog] = useState({ open: false, order: null });
  const [rejectionReason, setRejectionReason] = useState("");
  const [processingOrderId, setProcessingOrderId] = useState(null);
  const [processingCommissions, setProcessingCommissions] = useState(false);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    confirmedOrders: 0,
    rejectedOrders: 0,
    totalRevenue: 0,
  });

  // Fetch all orders from cartOrders collection
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError("");

      const ordersQuery = query(
        collection(db, "cartOrders"),
        orderBy("createdAt", "desc")
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const totalOrders = ordersData.length;
      const pendingOrders = ordersData.filter(
        (o) => o.orderStatus === "pending"
      ).length;
      const confirmedOrders = ordersData.filter(
        (o) => o.orderStatus === "confirmed"
      ).length;
      const rejectedOrders = ordersData.filter(
        (o) => o.orderStatus === "rejected"
      ).length;
      const totalRevenue = ordersData
        .filter((o) => o.orderStatus === "confirmed")
        .reduce(
          (sum, order) => sum + (order.totalAmount || order.grandTotal),
          0
        );

      setOrders(ordersData);
      setStats({
        totalOrders,
        pendingOrders,
        confirmedOrders,
        rejectedOrders,
        totalRevenue,
      });
    } catch (error) {
      setError("Failed to fetch orders: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Reject order
  const handleRejectOrder = async (order) => {
    if (!rejectionReason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }

    try {
      setError("");

      const orderRef = doc(db, "cartOrders", order.id);
      await updateDoc(orderRef, {
        orderStatus: "rejected",
        paymentStatus: "rejected",
        rejectedAt: new Date().toISOString(),
        rejectedBy: "admin",
        rejectionReason: rejectionReason.trim(),
      });

      setSuccess(
        `Order rejected for ${order.buyerName}. Reason: ${rejectionReason}`
      );
      setRejectDialog({ open: false, order: null });
      setRejectionReason("");
      fetchOrders();
    } catch (error) {
      setError("Failed to reject order: " + error.message);
    }
  };

  const handleConfirmOrder = async (order) => {
    try {
      setError("");
      setProcessingCommissions(true);
      setProcessingOrderId(order.cartOrderId);
      const result = await confirmCartOrder(order, { confirmedBy: "admin" });
      if (!result.success) {
        throw new Error(result.error || "Confirmation failed");
      }
      setSuccess(`Order confirmed for ${order.buyerName}!`);
      setConfirmDialog({ open: false, order: null });
      fetchOrders();
    } catch (error) {
      console.error("Error confirming order:", error);
      setError("Failed to confirm order: " + error.message);
    } finally {
      setProcessingCommissions(false);
      setProcessingOrderId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  // Download invoice PDF
  const handleDownloadInvoice = async (order) => {
    try {
      // Check if PDF URL exists in order document
      if (order.pdfUrl) {
        // Open the PDF URL directly
        window.open(order.pdfUrl, "_blank");
        setSuccess(`Invoice ${order.invoiceNumber} opened successfully!`);
        return;
      }

      // If no PDF URL, try to fetch from invoices collection
      const invoicesQuery = query(
        collection(db, "invoices"),
        where("orderId", "==", order.cartOrderId)
      );
      const invoicesSnapshot = await getDocs(invoicesQuery);
      
      if (!invoicesSnapshot.empty) {
        const invoiceDoc = invoicesSnapshot.docs[0];
        const invoiceData = invoiceDoc.data();
        if (invoiceData.pdfUrl) {
          window.open(invoiceData.pdfUrl, "_blank");
          setSuccess(`Invoice ${order.invoiceNumber} opened successfully!`);
          return;
        }
      }

      // Fallback: Generate and open HTML invoice in new window for printing/saving as PDF
      const invoiceHTML = generateInvoiceHTML(order);
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(invoiceHTML);
        printWindow.document.close();
        // Wait for content to load, then trigger print dialog (which allows saving as PDF)
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 250);
        };
        setSuccess(`Invoice ${order.invoiceNumber} opened for printing/saving as PDF!`);
      } else {
        // If popup blocked, download as HTML file
        const blob = new Blob([invoiceHTML], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${order.invoiceNumber || "Invoice"}_${order.cartOrderId}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setSuccess(`Invoice ${order.invoiceNumber} downloaded (HTML file)!`);
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      setError("Failed to download invoice: " + error.message);
    }
  };

  // Generate invoice HTML (same format as server-side)
  const generateInvoiceHTML = (order) => {
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(amount || 0);
    };

    const formatInvoiceDate = (dateString) => {
      if (!dateString) return new Date().toLocaleDateString("en-IN");
      return new Date(dateString).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    // Build items rows with detailed pricing
    let itemsHTML = "";
    let serialNo = 1;
    let totalMRP = 0;
    let totalSavings = 0;

    // Add items
    if (order.items && order.items.length > 0) {
      order.items.forEach((item) => {
        const quantity = item.quantity || 1;
        // Use compareAtPrice as MRP (that's what the frontend stores)
        const mrp = item.compareAtPrice || item.mrp || item.sellingPrice || item.price || 0;
        const price = item.sellingPrice || item.price || 0;
        const itemSavings = (mrp - price) * quantity;
        const total = price * quantity;
        
        totalMRP += mrp * quantity;
        totalSavings += itemSavings;

        // Get GST info for the item
        const itemGstPercentage = (item.gstPercentage !== undefined && item.gstPercentage !== null && item.gstPercentage !== '') ? Number(item.gstPercentage) : 0;
        const itemHsnCode = item.hsnCode || '-';
        const itemGstNumber = item.gstNumber || '-';
        
        itemsHTML += `
          <tr style="${serialNo % 2 === 0 ? "background-color: #f9f9f9;" : ""}">
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: center;">${serialNo++}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">
              <strong>${item.name}</strong>
              ${item.category ? `<br><span style="color: #666; font-size: 11px;">${item.category}</span>` : ""}
            </td>
            <td style="padding: 8px; border-bottom: 1px solid #e0e0e0; text-align: center; font-size: 11px;">${itemHsnCode}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: center;">${quantity}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: right; ${mrp > price ? "color: #999; text-decoration: line-through;" : ""}">${formatCurrency(mrp)}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: right;">${formatCurrency(price)}</td>
            <td style="padding: 8px; border-bottom: 1px solid #e0e0e0; text-align: center; font-size: 11px;">${itemGstPercentage}%</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: right; color: ${itemSavings > 0 ? "#00BFFF" : "#999"};">${itemSavings > 0 ? formatCurrency(itemSavings) : "-"}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: right; font-weight: 600;">${formatCurrency(total)}</td>
          </tr>
        `;
      });
    }

    // Add packages
    if (order.packages && order.packages.length > 0) {
      order.packages.forEach((pkg) => {
        const quantity = pkg.quantity || 1;
        const price = pkg.price || 0;
        const total = price * quantity;
        // Get GST info for the package
        const pkgGstPercentage = (pkg.gstPercentage !== undefined && pkg.gstPercentage !== null && pkg.gstPercentage !== '') ? Number(pkg.gstPercentage) : 0;
        const pkgHsnCode = pkg.hsnCode || '-';
        
        itemsHTML += `
          <tr style="${serialNo % 2 === 0 ? "background-color: #f9f9f9;" : ""}">
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: center;">${serialNo++}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">
              <strong>${pkg.name}</strong>
              <br><span style="color: #666; font-size: 11px;">${pkg.category || "Package"}</span>
            </td>
            <td style="padding: 8px; border-bottom: 1px solid #e0e0e0; text-align: center; font-size: 11px;">${pkgHsnCode}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: center;">${quantity}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: right;">${formatCurrency(price)}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: right;">${formatCurrency(price)}</td>
            <td style="padding: 8px; border-bottom: 1px solid #e0e0e0; text-align: center; font-size: 11px;">${pkgGstPercentage}%</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: right; color: #999;">-</td>
            <td style="padding: 10px; border-bottom: 1px solid #e0e0e0; text-align: right; font-weight: 600;">${formatCurrency(total)}</td>
          </tr>
        `;
      });
    }

    // Calculate detailed totals
    const appliedDiscounts = order.appliedDiscounts || {};
    const itemsTotal = order.itemTotal || order.subtotal || 0;
    const gstAmount = order.gstAmount || appliedDiscounts.gstAmount || 0;
    const subtotal = order.subtotal || 0;
    const cashbackDiscount = appliedDiscounts.cashbackDiscount || 0;
    const commissionDiscount = appliedDiscounts.commissionDiscount || appliedDiscounts.pointsDiscount || 0;
    const adminFee = appliedDiscounts.adminFee || appliedDiscounts.pointsAdminFee || 0;
    const totalAmount = order.totalAmount || order.grandTotal || 0;
    
    // Use calculated totalMRP from items, fallback to stored totalMRP from order
    const finalTotalMRP = totalMRP > 0 ? totalMRP : (order.totalMRP || 0);
    const storedPriceSavings = order.priceSavings || 0;
    const mrpSavings = finalTotalMRP > 0 ? finalTotalMRP - (itemsTotal - gstAmount) : (storedPriceSavings || totalSavings);
    const totalSaved = mrpSavings + cashbackDiscount + commissionDiscount;

    // Build address HTML
    const customerAddress = order.shippingAddress;
    const addressHTML = customerAddress ? `
      <p style="margin: 0; line-height: 1.6;">
        <strong>${customerAddress.fullName || order.buyerName}</strong><br>
        ${customerAddress.addressLine1 || ""}<br>
        ${customerAddress.addressLine2 ? customerAddress.addressLine2 + "<br>" : ""}
        ${customerAddress.city || ""}, ${customerAddress.state || ""} - ${customerAddress.pincode || ""}<br>
        ${customerAddress.phone ? "Phone: " + customerAddress.phone : ""}
      </p>
    ` : `
      <p style="margin: 0; line-height: 1.6;">
        <strong>${order.buyerName}</strong><br>
        ${order.buyerEmail}
      </p>
    `;

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invoice ${order.invoiceNumber}</title>
  <style>
    @media print {
      body { margin: 0; }
      .no-print { display: none; }
    }
  </style>
</head>
<body style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 20px; background-color: #f5f5f5;">
  <div class="no-print" style="text-align: center; margin-bottom: 20px;">
    <button onclick="window.print()" style="padding: 10px 30px; font-size: 16px; background: #001A4D; color: white; border: none; border-radius: 8px; cursor: pointer;">
      🖨️ Print Invoice
    </button>
  </div>
  <div style="max-width: 800px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 0 20px rgba(0,0,0,0.1);">
    
    <!-- Header -->
    <div style="background: linear-gradient(135deg, #001A4D 0%, #050F24 100%); padding: 20px; text-align: center;">
      <div style="display: flex; align-items: center; justify-content: center; gap: 15px;">
        <img src="https://infinitywhale.com/infinitywhalelogo.png" alt="Logo" style="width: 50px; height: 50px; border-radius: 8px;" onerror="this.style.display='none'">
        <h1 style="color: #ffffff; margin: 0; font-size: 28px; letter-spacing: 2px;">INFINITY WHALE</h1>
      </div>
    </div>

    <!-- Invoice Title -->
    <div style="background-color: #F0F8FF; padding: 20px; text-align: center; border-bottom: 3px solid #001A4D;">
      <h2 style="color: #001A4D; margin: 0; font-size: 24px;">TAX INVOICE</h2>
    </div>

    <!-- Invoice Details -->
    <div style="padding: 30px;">
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
        <tr>
          <td style="width: 50%; vertical-align: top; padding-right: 20px;">
            <h3 style="color: #001A4D; margin: 0 0 10px 0; font-size: 14px; text-transform: uppercase;">Invoice Details</h3>
            <p style="margin: 0; line-height: 1.8;">
              <strong>Invoice No:</strong> ${order.invoiceNumber || "N/A"}<br>
              <strong>Invoice Date:</strong> ${formatInvoiceDate(order.confirmedAt)}<br>
              <strong>Order ID:</strong> ${order.cartOrderId || order.orderId || "N/A"}<br>
              <strong>Order Date:</strong> ${formatInvoiceDate(order.createdAt)}
            </p>
          </td>
          <td style="width: 50%; vertical-align: top; padding-left: 20px; border-left: 1px solid #e0e0e0;">
            <h3 style="color: #001A4D; margin: 0 0 10px 0; font-size: 14px; text-transform: uppercase;">Bill To / Ship To</h3>
            ${addressHTML}
          </td>
        </tr>
      </table>

      <!-- Items Table -->
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
        <thead>
          <tr style="background-color: #001A4D;">
            <th style="padding: 12px 8px; color: #ffffff; text-align: center; font-size: 11px; width: 40px;">S.No</th>
            <th style="padding: 12px 8px; color: #ffffff; text-align: left; font-size: 11px;">Description</th>
            <th style="padding: 12px 6px; color: #ffffff; text-align: center; font-size: 10px; width: 60px;">HSN Code</th>
            <th style="padding: 12px 8px; color: #ffffff; text-align: center; font-size: 11px; width: 40px;">Qty</th>
            <th style="padding: 12px 8px; color: #ffffff; text-align: right; font-size: 11px; width: 70px;">MRP</th>
            <th style="padding: 12px 8px; color: #ffffff; text-align: right; font-size: 11px; width: 70px;">Price</th>
            <th style="padding: 12px 6px; color: #ffffff; text-align: center; font-size: 10px; width: 50px;">GST %</th>
            <th style="padding: 12px 8px; color: #ffffff; text-align: right; font-size: 11px; width: 60px;">Saved</th>
            <th style="padding: 12px 8px; color: #ffffff; text-align: right; font-size: 11px; width: 80px;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHTML}
        </tbody>
      </table>

      <!-- Detailed Totals -->
      <div style="display: flex; justify-content: flex-end;">
        <table style="width: 350px; border-collapse: collapse;">
          ${finalTotalMRP > 0 ? `
          <tr>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #666;">Total MRP:</td>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #666;">${formatCurrency(finalTotalMRP)}</td>
          </tr>
          ` : ""}
          <tr>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0;">Items Total:</td>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0;">${formatCurrency(itemsTotal)}</td>
          </tr>
          ${gstAmount > 0 ? `
          <tr>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #666;">GST on Items:</td>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #666;">${formatCurrency(gstAmount)}</td>
          </tr>
          ` : ""}
          <tr>
            <td style="padding: 10px; text-align: right; border-bottom: 1px solid #e0e0e0; font-weight: 600;">Subtotal:</td>
            <td style="padding: 10px; text-align: right; border-bottom: 1px solid #e0e0e0; font-weight: 600;">${formatCurrency(subtotal)}</td>
          </tr>
          ${cashbackDiscount > 0 ? `
          <tr>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #00BFFF;">Cashback Discount:</td>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #00BFFF; font-weight: 600;">-${formatCurrency(cashbackDiscount)}</td>
          </tr>
          ` : ""}
          ${commissionDiscount > 0 ? `
          <tr>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #00BFFF;">Points Wallet discount:</td>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #00BFFF; font-weight: 600;">-${formatCurrency(commissionDiscount)}</td>
          </tr>
          ` : ""}
          ${adminFee > 0 ? `
          <tr>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #D4AF37;">Points Wallet admin fee (10%):</td>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #D4AF37;">+${formatCurrency(adminFee)}</td>
          </tr>
          ` : ""}
          ${mrpSavings > 0 ? `
          <tr>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #00BFFF;">Price Savings (MRP):</td>
            <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; color: #00BFFF; font-weight: 600;">-${formatCurrency(mrpSavings)}</td>
          </tr>
          ` : ""}
          <tr style="background-color: #F0F8FF;">
            <td style="padding: 15px; text-align: right; font-size: 16px; font-weight: 700; color: #001A4D;">Grand Total:</td>
            <td style="padding: 15px; text-align: right; font-size: 18px; font-weight: 700; color: #001A4D;">${formatCurrency(totalAmount)}</td>
          </tr>
          ${totalSaved > 0 ? `
          <tr style="background-color: #00BFFF;">
            <td colspan="2" style="padding: 12px; text-align: center; color: #ffffff; font-weight: 700; font-size: 14px;">
              Total Saved: ${formatCurrency(totalSaved)}
            </td>
          </tr>
          ` : ""}
        </table>
      </div>

      <!-- Payment Status -->
      <div style="margin-top: 30px; padding: 20px; background-color: #e8f5e9; border-radius: 8px; border-left: 4px solid #00BFFF;">
        <p style="margin: 0; color: #2e7d32;">
          <strong>Payment Status:</strong> PAID<br>
          <strong>Payment Method:</strong> ${order.paymentMethod || order.paymentDetails?.method || "UPI"}<br>
          ${order.paymentDetails?.referenceNumber ? `<strong>Reference:</strong> ${order.paymentDetails.referenceNumber}` : ""}
        </p>
      </div>

      <!-- Thank You Note -->
      <div style="margin-top: 30px; text-align: center; padding: 20px; background-color: #f5f5f5; border-radius: 8px;">
        <p style="margin: 0; color: #666; font-size: 14px;">
          Thank you for shopping with <strong style="color: #001A4D;">InfinityWhale</strong>!<br>
          For any queries, contact us at <a href="mailto:support@infinitywhale.com" style="color: #001A4D;">support@infinitywhale.com</a>
        </p>
      </div>
    </div>

    <!-- Footer -->
    <div style="background-color: #050F24; padding: 20px; text-align: center;">
      <p style="color: rgba(255,255,255,0.9); margin: 0; font-size: 12px;">
        InfinityWhale<br>
        This is a computer-generated invoice and does not require a signature.
      </p>
    </div>
  </div>
</body>
</html>
    `.trim();
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "pending":
        return <Chip label="Pending" color="warning" size="small" />;
      case "confirmed":
        return <Chip label="Confirmed" color="success" size="small" />;
      case "rejected":
        return <Chip label="Rejected" color="error" size="small" />;
      case "cancelled":
        return <Chip label="Cancelled" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const formatOrderId = (longId) => {
    if (longId && longId.includes("CART-")) {
      const timestamp = longId.split("-")[1];
      const shortTimestamp = timestamp.slice(-6);
      return `CART${shortTimestamp}`;
    } else {
      return longId || "UNKNOWN";
    }
  };

  const getOrderSummary = (order) => {
    const itemCount = order.orderCounts?.totalItems || 0;
    const packageCount = order.orderCounts?.totalPackages || 0;
    const uniqueItems = order.orderCounts?.uniqueItems || 0;
    const uniquePackages = order.orderCounts?.uniquePackages || 0;

    let summary = `Order #${formatOrderId(order.cartOrderId)}`;

    if (itemCount > 0 && packageCount > 0) {
      summary += ` - ${uniqueItems} items (${itemCount} qty) + ${uniquePackages} packages (${packageCount} qty)`;
    } else if (itemCount > 0) {
      summary += ` - ${uniqueItems} items (${itemCount} qty)`;
    } else if (packageCount > 0) {
      summary += ` - ${uniquePackages} packages (${packageCount} qty)`;
    }

    return summary;
  };

  // Enhanced render item details function
  const renderItemDetails = (item) => (
    <Card key={item.id} sx={{ mb: 2, border: "1px solid #e0e0e0" }}>
      <CardContent>
        <Grid container spacing={2}>
          {/* Item Image and Basic Info */}
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: "center" }}>
              {item.imageUrl && (
                <Avatar
                  src={item.imageUrl}
                  alt={item.name}
                  sx={{ width: 120, height: 120, mx: "auto", mb: 1 }}
                  variant="rounded"
                />
              )}
              <Typography variant="h6" color="primary">
                {item.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                SKU: {item.sku || "N/A"}
              </Typography>
            </Box>
          </Grid>

          {/* Basic Details */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" gutterBottom color="primary">
              <ShoppingBag fontSize="small" sx={{ mr: 1 }} />
              Basic Details
            </Typography>
            <Typography variant="body2">
              <strong>Category:</strong> {item.category}
            </Typography>
            <Typography variant="body2">
              <strong>Brand:</strong> {item.brand || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Manufacturer:</strong> {item.manufacturer || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Price:</strong> {formatPrice(item.sellingPrice)}
            </Typography>
            <Typography variant="body2">
              <strong>Quantity:</strong> {item.quantity}
            </Typography>
            {(() => {
              const gstPercentage = (item.gstPercentage !== undefined && item.gstPercentage !== null && item.gstPercentage !== '') ? Number(item.gstPercentage) : 0;
              const basePrice = item.basePrice || item.sellingPrice || item.price || 0;
              const quantity = item.quantity || 1;
              const itemGstAmount = item.gstAmount || (gstPercentage > 0 ? (basePrice * gstPercentage / 100) * quantity : 0);
              
              if (gstPercentage > 0 || itemGstAmount > 0) {
                return (
                  <>
                    <Typography variant="body2">
                      <strong>Base Price:</strong> {formatPrice(basePrice)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "primary.main" }}>
                      <strong>GST ({gstPercentage}%):</strong> {formatPrice(itemGstAmount)}
                    </Typography>
                  </>
                );
              }
              return null;
            })()}
            <Typography variant="body2">
              <strong>Total:</strong>{" "}
              {formatPrice(item.sellingPrice * item.quantity)}
            </Typography>
          </Grid>

          {/* Selected Options */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" gutterBottom color="primary">
              <Palette fontSize="small" sx={{ mr: 1 }} />
              Customer Selection
            </Typography>
            {item.selectedOptions && (
              <>
                <Typography variant="body2">
                  <strong>Color:</strong>{" "}
                  {item.selectedOptions.color ||
                    item.selectedColor ||
                    "Not specified"}
                </Typography>
                <Typography variant="body2">
                  <strong>Size:</strong>{" "}
                  {item.selectedOptions.size ||
                    item.selectedSize ||
                    "Not specified"}
                </Typography>
              </>
            )}
            {item.colors && item.colors.length > 0 && (
              <Typography variant="body2">
                <strong>Available Colors:</strong> {item.colors.join(", ")}
              </Typography>
            )}
            {item.sizes && item.sizes.length > 0 && (
              <Typography variant="body2">
                <strong>Available Sizes:</strong> {item.sizes.join(", ")}
              </Typography>
            )}
          </Grid>

          {/* Product Details */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" gutterBottom color="primary">
              <Info fontSize="small" sx={{ mr: 1 }} />
              Product Info
            </Typography>
            <Typography variant="body2">
              <strong>Weight:</strong> {item.weight || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Dimensions:</strong> {item.dimensions || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Material:</strong> {item.material || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Warranty:</strong> {item.warranty || "N/A"}
            </Typography>
            {item.rating > 0 && (
              <Typography variant="body2">
                <Star fontSize="small" sx={{ color: "gold", mr: 0.5 }} />
                <strong>Rating:</strong> {item.rating}/5
              </Typography>
            )}
          </Grid>

          {/* Description */}
          {item.description && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" gutterBottom color="primary">
                Description
              </Typography>
              <Typography variant="body2">{item.description}</Typography>
            </Grid>
          )}

          {/* Additional Details in Accordions */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />

            {/* Specifications */}
            {(item.specifications ||
              item.features?.length > 0 ||
              item.nutritionalInfo ||
              item.ingredients) && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">
                    Specifications & Features
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {item.specifications && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Specifications:</strong> {item.specifications}
                    </Typography>
                  )}
                  {item.features && item.features.length > 0 && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Features:</strong> {item.features.join(", ")}
                    </Typography>
                  )}
                  {item.nutritionalInfo && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Nutritional Info:</strong> {item.nutritionalInfo}
                    </Typography>
                  )}
                  {item.ingredients && (
                    <Typography variant="body2">
                      <strong>Ingredients:</strong> {item.ingredients}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Additional Info */}
            {(item.careInstructions ||
              item.storageConditions ||
              item.allergenInfo ||
              item.countryOfOrigin) && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Care & Storage</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {item.careInstructions && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Care Instructions:</strong>{" "}
                      {item.careInstructions}
                    </Typography>
                  )}
                  {item.storageConditions && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Storage:</strong> {item.storageConditions}
                    </Typography>
                  )}
                  {item.allergenInfo && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Allergen Info:</strong> {item.allergenInfo}
                    </Typography>
                  )}
                  {item.countryOfOrigin && (
                    <Typography variant="body2">
                      <strong>Country of Origin:</strong> {item.countryOfOrigin}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Business Details */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2">Business Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Base Price:</strong> {formatPrice(item.basePrice)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Selling Price:</strong>{" "}
                      {formatPrice(item.sellingPrice)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Profit Margin:</strong>{" "}
                      {formatPrice(item.profitMargin)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Stock Quantity:</strong> {item.stockQuantity}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Referral Share:</strong>{" "}
                      {item.referralShare ? "Yes" : "No"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Active:</strong> {item.isActive ? "Yes" : "No"}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Enhanced View Order Dialog
  const renderViewOrderDialog = () => (
    <Dialog
      open={viewDialog.open}
      onClose={() => setViewDialog({ open: false, order: null })}
      maxWidth="xl"
      fullWidth
      sx={{ "& .MuiDialog-paper": { height: "90vh" } }}
    >
      <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">
            Order Details - {formatOrderId(viewDialog.order?.cartOrderId)}
          </Typography>
          {getStatusChip(viewDialog.order?.orderStatus)}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {viewDialog.order && (
          <Box sx={{ height: "100%", overflow: "auto" }}>
            {/* Order Summary Header */}
            <Box sx={{ p: 3, bgcolor: "grey.50" }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <Person sx={{ mr: 1 }} />
                        Customer Information
                      </Typography>
                      <Typography variant="body1">
                        <strong>{viewDialog.order.buyerName}</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
                      >
                        <Email fontSize="small" sx={{ mr: 1 }} />
                        {viewDialog.order.buyerEmail}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        User ID: {viewDialog.order.buyerId}
                      </Typography>
                      {viewDialog.order.userDetails?.myReferralId && (
                        <Typography variant="body2">
                          <strong>Referral ID:</strong>{" "}
                          {viewDialog.order.userDetails.myReferralId}
                        </Typography>
                      )}
                      {viewDialog.order.userDetails?.referralIdUsed && (
                        <Typography variant="body2">
                          <strong>Used Referral:</strong>{" "}
                          {viewDialog.order.userDetails.referralIdUsed}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <CreditCard sx={{ mr: 1 }} />
                        Order Summary
                      </Typography>
                      <Typography variant="body2">
                        <strong>Order ID:</strong>{" "}
                        {viewDialog.order.cartOrderId}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Order Date:</strong>{" "}
                        {formatDate(viewDialog.order.createdAt)}
                      </Typography>
                      {viewDialog.order.confirmedAt && (
                        <Typography variant="body2">
                          <strong>Confirmed:</strong>{" "}
                          {formatDate(viewDialog.order.confirmedAt)}
                        </Typography>
                      )}
                      {viewDialog.order.rejectedAt && (
                        <Typography variant="body2">
                          <strong>Rejected:</strong>{" "}
                          {formatDate(viewDialog.order.rejectedAt)}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        <strong>Type:</strong> {viewDialog.order.orderType}
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        Total:{" "}
                        {formatPrice(
                          viewDialog.order.totalAmount ||
                            viewDialog.order.grandTotal
                        )}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <Inventory sx={{ mr: 1 }} />
                        Order Contents
                      </Typography>
                      <Typography variant="body2">
                        <strong>Items:</strong>{" "}
                        {viewDialog.order.orderCounts?.uniqueItems || 0} types (
                        {viewDialog.order.orderCounts?.totalItems || 0} qty)
                      </Typography>
                      <Typography variant="body2">
                        <strong>Packages:</strong>{" "}
                        {viewDialog.order.orderCounts?.uniquePackages || 0}{" "}
                        types (
                        {viewDialog.order.orderCounts?.totalPackages || 0} qty)
                      </Typography>
                      <Typography variant="body2">
                        <strong>Item Total:</strong>{" "}
                        {formatPrice(viewDialog.order.itemTotal || 0)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Package Total:</strong>{" "}
                        {formatPrice(viewDialog.order.packageTotal || 0)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Subtotal:</strong>{" "}
                        {formatPrice(viewDialog.order.subtotal)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Rejection Info */}
            {viewDialog.order.orderStatus === "rejected" &&
              viewDialog.order.rejectionReason && (
                <Alert severity="error" sx={{ m: 3 }}>
                  <Typography variant="body1">
                    <strong>Rejection Reason:</strong>{" "}
                    {viewDialog.order.rejectionReason}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rejected by: {viewDialog.order.rejectedBy} on{" "}
                    {formatDate(viewDialog.order.rejectedAt)}
                  </Typography>
                </Alert>
              )}

            {/* Applied Discounts */}
            {viewDialog.order.hasAppliedDiscounts &&
              viewDialog.order.appliedDiscounts && (
                <Box sx={{ p: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <Discount sx={{ mr: 1 }} />
                        Applied Discounts
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Total Discount:</strong>{" "}
                            {formatPrice(
                              viewDialog.order.appliedDiscounts.totalDiscount
                            )}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Used Cashback:</strong>{" "}
                            {viewDialog.order.appliedDiscounts.usedCashback
                              ? "Yes"
                              : "No"}
                          </Typography>
                          {viewDialog.order.appliedDiscounts.usedCashback && (
                            <Typography variant="body2">
                              <strong>Cashback Amount:</strong>{" "}
                              {formatPrice(
                                viewDialog.order.appliedDiscounts
                                  .cashbackDiscount
                              )}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Used Commission:</strong>{" "}
                            {viewDialog.order.appliedDiscounts.usedCommission
                              ? "Yes"
                              : "No"}
                          </Typography>
                          {viewDialog.order.appliedDiscounts.usedCommission && (
                            <>
                              <Typography variant="body2">
                                <strong>Commission Discount:</strong>{" "}
                                {formatPrice(
                                  viewDialog.order.appliedDiscounts
                                    .commissionDiscount
                                )}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Admin Fee:</strong>{" "}
                                {formatPrice(
                                  viewDialog.order.appliedDiscounts
                                    .commissionAdminFee
                                )}
                              </Typography>
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}

            {/* Shipping Address */}
            {viewDialog.order.shippingAddress && (
              <Box sx={{ p: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      <LocalShipping sx={{ mr: 1 }} />
                      Shipping Address
                    </Typography>
                    <Typography variant="body1">
                      <strong>
                        {viewDialog.order.shippingAddress.fullName}
                      </strong>
                    </Typography>
                    <Typography variant="body2">
                      {viewDialog.order.shippingAddress.addressLine1}
                    </Typography>
                    {viewDialog.order.shippingAddress.addressLine2 && (
                      <Typography variant="body2">
                        {viewDialog.order.shippingAddress.addressLine2}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      {viewDialog.order.shippingAddress.city},{" "}
                      {viewDialog.order.shippingAddress.state} -{" "}
                      {viewDialog.order.shippingAddress.pincode}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ display: "flex", alignItems: "center", mt: 1 }}
                    >
                      <Phone fontSize="small" sx={{ mr: 1 }} />
                      {viewDialog.order.shippingAddress.phone}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Payment Details */}
            {viewDialog.order.paymentDetails && (
              <Box sx={{ p: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      <Payment sx={{ mr: 1 }} />
                      Payment Information
                    </Typography>
                    <Typography variant="body2">
                      <strong>Payment Method:</strong>{" "}
                      {viewDialog.order.paymentMethod ||
                        viewDialog.order.paymentDetails.method}
                    </Typography>
                    {viewDialog.order.paymentDetails.upiId && (
                      <Typography variant="body2">
                        <strong>UPI ID:</strong>{" "}
                        {viewDialog.order.paymentDetails.upiId}
                      </Typography>
                    )}
                    {viewDialog.order.paymentDetails.referenceNumber && (
                      <Typography variant="body2">
                        <strong>Transaction Reference:</strong>{" "}
                        {viewDialog.order.paymentDetails.referenceNumber}
                      </Typography>
                    )}
                    {viewDialog.order.paymentDetails.transactionDetails && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Transaction Details:</strong>{" "}
                        {viewDialog.order.paymentDetails.transactionDetails}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      <strong>Payment Status:</strong>
                      <Chip
                        label={viewDialog.order.paymentStatus}
                        color={
                          viewDialog.order.paymentStatus === "confirmed"
                            ? "success"
                            : viewDialog.order.paymentStatus === "pending"
                            ? "warning"
                            : "error"
                        }
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Order Items */}
            {viewDialog.order.items && viewDialog.order.items.length > 0 && (
              <Box sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  color="primary"
                  gutterBottom
                  sx={{ mb: 2 }}
                >
                  <ShoppingBag sx={{ mr: 1 }} />
                  Order Items ({viewDialog.order.items.length})
                </Typography>
                {viewDialog.order.items.map((item, index) =>
                  renderItemDetails(item)
                )}
              </Box>
            )}

            {/* Order Packages */}
            {viewDialog.order.packages &&
              viewDialog.order.packages.length > 0 && (
                <Box sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    color="primary"
                    gutterBottom
                    sx={{ mb: 2 }}
                  >
                    <Inventory sx={{ mr: 1 }} />
                    Order Packages ({viewDialog.order.packages.length})
                  </Typography>
                  {viewDialog.order.packages.map((pkg, index) => (
                    <Card
                      key={pkg.id || index}
                      sx={{ mb: 2, border: "1px solid #e0e0e0" }}
                    >
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={3}>
                            <Box sx={{ textAlign: "center" }}>
                              {pkg.imageUrl && (
                                <Avatar
                                  src={pkg.imageUrl}
                                  alt={pkg.name}
                                  sx={{
                                    width: 120,
                                    height: 120,
                                    mx: "auto",
                                    mb: 1,
                                  }}
                                  variant="rounded"
                                />
                              )}
                              <Typography variant="h6" color="primary">
                                {pkg.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Package
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={9}>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>Price:</strong>{" "}
                                  {formatPrice(pkg.price)}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Quantity:</strong> {pkg.quantity}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Total:</strong>{" "}
                                  {formatPrice(pkg.price * pkg.quantity)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>Category:</strong>{" "}
                                  {pkg.category || "Package"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Description:</strong>{" "}
                                  {pkg.description || "N/A"}
                                </Typography>
                                {pkg.isSubItemPackage && (
                                  <Typography variant="body2">
                                    <strong>Type:</strong> Sub-items Package
                                  </Typography>
                                )}
                              </Grid>
                            </Grid>

                            {/* Sub-items Details */}
                            {pkg.isSubItemPackage && pkg.subItemsData && pkg.subItemsData.subItems && (
                              <Box sx={{ mt: 2, p: 2, bgcolor: "rgba(0, 114, 206, 0.1)", borderRadius: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                                  Selected Sub-items:
                                </Typography>
                                <Grid container spacing={1}>
                                  {pkg.subItemsData.subItems.map((subItem, subIndex) => (
                                    <Grid item xs={12} key={subIndex}>
                                      <Box sx={{ 
                                        display: "flex", 
                                        justifyContent: "space-between", 
                                        alignItems: "center",
                                        p: 1,
                                        bgcolor: "white",
                                        borderRadius: 1,
                                        mb: 1
                                      }}>
                                        <Box>
                                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {subItem.itemName}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {formatPrice(subItem.itemPrice)} × {subItem.selectedQuantity}
                                          </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {formatPrice(subItem.totalPrice)}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  ))}
                                </Grid>
                                
                                {/* Sub-items Summary */}
                                <Box sx={{ mt: 2, p: 2, bgcolor: "white", borderRadius: 1 }}>
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                      <Typography variant="body2">
                                        <strong>Sub-items Total:</strong> {formatPrice(pkg.subItemsData.subItemsTotal || 0)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="body2">
                                        <strong>Activation Fee:</strong> {formatPrice(pkg.subItemsData.activationFee || 0)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: "primary.main" }}>
                                        <strong>Package Total:</strong> {formatPrice(pkg.price)}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

            {/* Commission Processing Status */}
            {viewDialog.order.orderStatus === "confirmed" && (
              <Box sx={{ p: 3 }}>
                <Alert severity="success">
                  <Typography variant="body2">
                    ✅ <strong>Order Processed Successfully:</strong> This
                    confirmed order has been automatically processed with:
                    <br />• Affiliate referral points (up to 100% of profit margin, 10 levels)
                    <br />• Undistributed profit when referral chain is incomplete
                    <br />• First-time package purchase cashback (if applicable)
                    <br />• Applied customer discounts (cashback/commission
                    usage)
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: 1, borderColor: "divider", p: 2 }}>
        <Button
          onClick={() => setViewDialog({ open: false, order: null })}
          variant="contained"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" gutterBottom>
          Orders
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchOrders}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Important Notice */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>UPI / manual payments:</strong> verify the transaction ID in
          the order details, then <strong>Confirm</strong> or <strong>Reject</strong>.
          <strong> Cashfree</strong> orders confirm automatically when the gateway
          is active.
        </Typography>
      </Alert>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <ShoppingCart color="info" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {stats.totalOrders}
              </Typography>
              <Typography variant="body1">Total Orders</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Pending color="warning" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {stats.pendingOrders}
              </Typography>
              <Typography variant="body1">Pending Orders</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {stats.confirmedOrders}
              </Typography>
              <Typography variant="body1">Confirmed Orders</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <AccountBalance color="primary" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h4" color="primary">
                {formatPrice(stats.totalRevenue)}
              </Typography>
              <Typography variant="body1">Total Revenue</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Orders Table */}
      {loading ? (
        <Box textAlign="center" sx={{ mt: 4 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading orders...
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Order Details</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Confirmed Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {order.buyerName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {order.buyerEmail}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {getOrderSummary(order)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total:{" "}
                        {formatPrice(order.totalAmount || order.grandTotal)}
                        {(order.paymentDetails?.transactionDetails || order.paymentDetails?.referenceNumber) && (
                          <Chip
                            label="Transaction ID"
                            size="small"
                            color="info"
                            sx={{ ml: 1, fontSize: "0.65rem", height: 20 }}
                          />
                        )}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6" color="primary">
                      {formatPrice(order.totalAmount || order.grandTotal)}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(order.orderStatus)}</TableCell>
                  <TableCell>
                    {!cartOrderQualifiesForInvoice(order) ? (
                      <Typography variant="caption" color="text.secondary">
                        N/A
                      </Typography>
                    ) : order.orderStatus === "confirmed" && order.invoiceNumber ? (
                      <Box>
                        <Chip
                          icon={<Receipt sx={{ fontSize: 16 }} />}
                          label={order.invoiceNumber}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PictureAsPdf sx={{ fontSize: 16 }} />}
                          onClick={() => handleDownloadInvoice(order)}
                          sx={{
                            fontSize: "0.7rem",
                            textTransform: "none",
                            display: "flex",
                            alignItems: "center",
                            p: "2px 8px",
                            minWidth: "auto",
                            bgcolor: "#d32f2f",
                            "&:hover": { bgcolor: "#b71c1c" },
                          }}
                        >
                          PDF
                        </Button>
                      </Box>
                    ) : order.orderStatus === "confirmed" ? (
                      <Typography variant="caption" color="text.secondary">
                        Generating...
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(order.createdAt)}</TableCell>
                  <TableCell>{formatDate(order.confirmedAt)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => setViewDialog({ open: true, order })}
                      >
                        View
                      </Button>

                      {order.orderStatus === "pending" &&
                        orderNeedsAdminPaymentApproval(order) && (
                          <>
                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              startIcon={<CheckCircle />}
                              onClick={() =>
                                setConfirmDialog({ open: true, order })
                              }
                              disabled={
                                processingCommissions &&
                                processingOrderId === order.cartOrderId
                              }
                            >
                              Confirm
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() =>
                                setRejectDialog({ open: true, order })
                              }
                            >
                              Reject
                            </Button>
                          </>
                        )}

                      {order.orderStatus === "pending" &&
                        !orderNeedsAdminPaymentApproval(order) && (
                          <Chip
                            label="Awaiting Cashfree"
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                        )}

                      {order.orderStatus === "confirmed" && (
                        <Chip label="Completed" color="success" size="small" />
                      )}

                      {order.orderStatus === "rejected" && (
                        <Chip label="Rejected" color="error" size="small" />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No orders found yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, order: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Order</DialogTitle>
        <DialogContent>
          {confirmDialog.order && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to confirm this order?
              </Typography>
              <Box sx={{ my: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Typography variant="h6">
                  {getOrderSummary(confirmDialog.order)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customer: {confirmDialog.order.buyerName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Email: {confirmDialog.order.buyerEmail}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Amount:{" "}
                  {formatPrice(
                    confirmDialog.order.totalAmount ||
                      confirmDialog.order.grandTotal
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Order Date: {formatDate(confirmDialog.order.createdAt)}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This will mark the order as confirmed and the payment as
                received.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, order: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleConfirmOrder(confirmDialog.order)}
            variant="contained"
            color="success"
            disabled={
              processingCommissions ||
              processingOrderId === confirmDialog.order?.cartOrderId
            }
          >
            {processingOrderId === confirmDialog.order?.cartOrderId
              ? "Processing Order..."
              : "Confirm Order"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={() => {
          setRejectDialog({ open: false, order: null });
          setRejectionReason("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Order</DialogTitle>
        <DialogContent>
          {rejectDialog.order && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to reject this order?
              </Typography>
              <Box sx={{ my: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Typography variant="h6">
                  {getOrderSummary(rejectDialog.order)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customer: {rejectDialog.order.buyerName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Email: {rejectDialog.order.buyerEmail}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Amount:{" "}
                  {formatPrice(
                    rejectDialog.order.totalAmount ||
                      rejectDialog.order.grandTotal
                  )}
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason for Rejection *"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejecting this order..."
                sx={{ mt: 2 }}
                required
              />

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                The customer will be notified about the rejection and the reason
                provided.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialog({ open: false, order: null });
              setRejectionReason("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleRejectOrder(rejectDialog.order)}
            variant="contained"
            color="error"
          >
            Reject Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced View Order Dialog */}
      {renderViewOrderDialog()}
    </Box>
  );
};

export default OrderManagement;
